import { catchError, finalize, last, shareReplay } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";

export const SKIP_CACHE_HEADER = "X-Skip-Cache-Interceptor";

@Injectable({
  providedIn: "root",
})
export class HttpCacheInterceptor implements HttpInterceptor {
  private _cache: Map<string, Observable<any>> = new Map();

  intercept(request: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestKey = this.getKey(request);
    const lastResponse = this._cache.get(requestKey);
    const contentType = request.headers.get("Content-Type");

    // Found a cached response so pipe the last response back
    if (lastResponse) {
      return lastResponse.pipe(last());
    }

    // Skip cache if it has this header
    let skipCaching = request.headers.has(SKIP_CACHE_HEADER);
    let requestHeaders = request.headers;

    // If doing a multipart upload don't cache the request!
    if (contentType && contentType === "multipart/form-data") {
      skipCaching = true;
      requestHeaders = request.headers.delete("Content-Type");
    } else if (!contentType) {
      requestHeaders = request.headers.append("Content-Type", "application/json");
    }

    // Clone the request in order to change the headers!
    const clonedRequest = request.clone({
      headers: requestHeaders,
    });

    // Catch errors for each request
    const responseHandle = handler.handle(clonedRequest).pipe(
      catchError((response: any) => {
        console.error(`Backend returned code ${response.status}, body was: ${response.message}`);
        this._cache.delete(requestKey);

        // Set error message(s)
        let errors: string[] = [];
        if (response.error && typeof response.error.message === "string") {
          errors.push(response.error.message);
        } else if (response.error && Array.isArray(response.error.message)) {
          errors = response.error.message;
        } else {
          errors.push(response.message);
        }

        return of(
          new HttpResponse({
            body: {
              success: false,
              errors,
            },
          })
        );
      }),
      finalize(() => {
        this._cache.delete(requestKey);
      }),
      shareReplay(1)
    );

    // Skip caching on this request
    if (!skipCaching) {
      this._cache.set(requestKey, responseHandle);
    }
    return responseHandle;
  }

  private getKey(request: HttpRequest<any>): string {
    let key = request.url;
    if (request.body) {
      Object.keys(request.body).forEach((k) => {
        key += `_${k}_${request.body[k]}`;
      });
    } else {
      request.params.keys().forEach((k) => {
        key += `_${k}_${request.params.get(k)}`;
      });
    }
    return key;
  }
}
